import { useEffect, useState } from "react";
import axios from "axios";
import { API, api } from "@/lib/api";
import { log } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import {
  AntecessorArticle,
  VaultGate,
  fileToBase64,
} from "@/components/antecessores/AntecessorSections";

export default function Antecessores() {
  const { isAdmin } = useAuth();
  // Initialise `granted` from isAdmin so admins skip the gate on first render
  // without a setState-in-effect. The effect below only handles the (rare)
  // case where role flips while the page is open.
  const [granted, setGranted] = useState(() => Boolean(isAdmin));
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);

  // Admins / masters auto-load records — fetch only, no setGranted-in-effect
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      const res = await api.get("/vault");
      if (!cancelled) setRecords(res.data);
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const unlock = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/vault/access`, { passphrase: pass });
      const res = await api.get("/vault", { headers: { "X-Vault-Passphrase": pass } });
      setRecords(res.data);
      setGranted(true);
    } catch (err) {
      log.warn("[ANTECESSORES] unlock rejected:", err.message);
      setError("The passphrase is incorrect. The vault remains sealed.");
    } finally {
      setLoading(false);
    }
  };

  const onUnlockKey = (e) => {
    if (e.key === "Enter") unlock();
  };

  const startEdit = (r) => {
    setDraft({ ...r });
    setEditingId(r.id);
  };

  const cancel = () => {
    setDraft(null);
    setEditingId(null);
  };

  const save = async () => {
    try {
      const res = await api.put(`/vault/${editingId}`, draft);
      setRecords((prev) => prev.map((r) => (r.id === editingId ? res.data : r)));
      cancel();
      toast.success("Antecessor sealed.");
    } catch (err) {
      log.warn("[ANTECESSORES] save failed:", err.message);
      toast.error("Update failed.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (max ~1.2MB).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (i) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
  };

  // Gate screen
  if (!granted) {
    return (
      <VaultGate
        pass={pass}
        setPass={setPass}
        error={error}
        loading={loading}
        onUnlock={unlock}
        onKeyDown={onUnlockKey}
      />
    );
  }

  // Granted view
  return (
    <div className="space-y-14" data-testid="vault-page">
      <header className="space-y-6">
        <div className="flex items-center gap-3">
          <span className="tiv-stamp">OMEGA·VAULT</span>
          <span className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            Quattuor Antetempus · Unsealed
          </span>
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          Antecessores
        </h1>
        <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
          Four entries. Four predate the hour. What follows was scratched into the underside of the
          first cosmogony — recovered by hand, transcribed in absence of light. Do not pronounce the
          names within earshot of an open window.
        </p>
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]">
          {records.length} records · {isAdmin ? "Archivist mode" : "Witness mode"}
        </div>
      </header>

      <div className="space-y-12">
        {records.map((r) => (
          <AntecessorArticle
            key={r.id}
            record={r}
            editingId={editingId}
            draft={draft}
            setDraft={setDraft}
            isAdmin={isAdmin}
            onEdit={startEdit}
            onSave={save}
            onCancel={cancel}
            onUpload={onUpload}
            removeImage={removeImage}
          />
        ))}
      </div>

      <div className="border-t border-[#333] pt-8 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        end of the four · do not speak the names
      </div>
    </div>
  );
}
